import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { products } from '../data/mock';
import { CheckCircle2, ArrowRight, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';

const HomePage = () => {
  const { addToCart } = useCart();

  // Featured: Gaming Headset RGB (matches djones featured product)
  const featuredProduct = products.find((p) => p.slug === 'gaming-headset-rgb');

  // Grid of 5 products under featured (djones-style)
  const productsGrid = [
    products.find((p) => p.slug === 'classic-black-graphic-drawing-tablet'),
    products.find((p) => p.slug === 'advanced-ebook-reader'),
    products.find((p) => p.slug === 'graphic-tablet-monitor-stylus'),
    products.find((p) => p.slug === 'wireless-bluetooth-gaming-mouse'),
    products.find((p) => p.slug === 'bluetooth-computer-speaker-system'),
  ].filter(Boolean);

  useEffect(() => {
    document.title = 'EC Tech Savvy | Latest Electronics, Smart Gadgets & Tech Innovations';
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <CartDrawer />

      <main>
        {/* ============================================
            HERO — Tech Innovations Await
           ============================================ */}
        <section className="relative h-[520px] md:h-[620px] w-full overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1601950214268-d063b21f586b?q=80&w=2670&auto=format&fit=crop"
            alt="Tech Innovations Await"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-indigo-900/60" />

          <div className="relative z-10 mx-auto flex h-full max-w-4xl flex-col items-center justify-center px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight leading-tight">
              Tech Innovations Await
            </h1>
            <p className="mt-5 text-lg md:text-xl text-gray-100 max-w-2xl">
              Stay ahead with the latest electronics and smart gadgets.
            </p>
            <Link
              to="/shop"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-8 py-3 font-semibold text-white shadow-lg transition hover:scale-105 hover:bg-indigo-500 focus:ring-4 focus:ring-white/40"
            >
              Shop Electronics
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* ============================================
            FUTURE-PROOF YOUR LIFE
           ============================================ */}
        <section className="bg-gray-50 py-20 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center bg-white rounded-3xl shadow-sm ring-1 ring-gray-100 px-6 py-12 sm:p-12 lg:p-16">
              <img
                className="h-80 w-full lg:h-[440px] lg:max-w-md rounded-2xl object-cover shadow-xl flex-shrink-0"
                src="https://images.unsplash.com/photo-1615743879132-3244ab6efcd1?q=80&w=2670&auto=format&fit=crop"
                alt="Future-Proof Your Life"
              />
              <div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-gray-900">
                  Future-Proof Your Life
                </h2>
                <p className="mt-5 text-lg text-gray-600">
                  Stay connected with the latest electronic innovations.
                </p>
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {[
                    'Latest technology',
                    'High-performance gadgets',
                    'Innovative solutions',
                    'User-friendly designs',
                    'Competitive pricing',
                    'Expertly curated selection',
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-gray-700">
                      <CheckCircle2 className="h-5 w-5 flex-none text-indigo-600 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10">
                  <Link
                    to="/shop"
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-500"
                  >
                    Discover Electronics <span aria-hidden="true">&rarr;</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================
            FEATURED PRODUCT (large hero card)
           ============================================ */}
        {featuredProduct && (
          <section className="bg-white py-16 sm:py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                <div className="w-full lg:w-1/2">
                  <div className="aspect-square w-full bg-gray-50 rounded-2xl overflow-hidden ring-1 ring-gray-100 flex items-center justify-center p-6">
                    <img
                      src={featuredProduct.image}
                      alt={featuredProduct.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </div>
                <div className="w-full lg:w-1/2 text-center lg:text-left">
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                    {featuredProduct.name}
                  </h2>
                  <p className="mt-4 text-3xl font-bold text-indigo-600">
                    ${featuredProduct.price.toFixed(2)}
                  </p>
                  <p className="mt-6 text-gray-600 text-base leading-relaxed">
                    {featuredProduct.description}
                  </p>
                  <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-3">
                    <Link
                      to={`/product/${featuredProduct.slug}`}
                      className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-indigo-600 text-white font-semibold shadow hover:bg-indigo-500 transition"
                    >
                      Buy Now
                    </Link>
                    <Link
                      to="/shop"
                      className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full border border-gray-300 text-gray-800 font-semibold hover:bg-gray-50 transition"
                    >
                      Browse More
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ============================================
            PRODUCT GRID (5 products like djones)
           ============================================ */}
        <section className="bg-gray-50 py-16 sm:py-20 border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              {productsGrid.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-xl ring-1 ring-gray-100 hover:shadow-lg transition overflow-hidden flex flex-col"
                >
                  <Link to={`/product/${product.slug}`} className="block bg-gray-50">
                    <img
                      className="w-full object-contain h-52 p-4"
                      src={product.image}
                      alt={product.name}
                    />
                  </Link>
                  <div className="p-4 flex flex-col flex-grow">
                    <Link
                      to={`/product/${product.slug}`}
                      className="block text-base font-semibold text-gray-900 hover:text-indigo-600 line-clamp-2 min-h-[3rem]"
                    >
                      {product.name}
                    </Link>
                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                      {product.description.slice(0, 80)}...
                    </p>
                    <div className="mt-3 text-indigo-600 font-bold text-lg">
                      ${product.price.toFixed(2)}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => addToCart(product)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-md hover:bg-indigo-700 transition"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Add
                      </button>
                      <Link
                        to={`/product/${product.slug}`}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 text-sm font-semibold rounded-md hover:bg-gray-50 transition"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white border border-gray-300 text-gray-900 font-semibold hover:bg-gray-50 transition"
              >
                Browse All Products
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ============================================
            FAQ — light, simple cards (djones style)
           ============================================ */}
        <section className="bg-white py-16 sm:py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center">
              Frequently Asked Questions
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  q: 'What is the Typical Shipping Time?',
                  a: 'Your order will be processed and shipped via USPS within 1 business day. Delivery is expected between 2 to 4 business days. For delays, contact customer care at (855) 684-7001.'
                },
                {
                  q: 'Is Shipping Free?',
                  a: 'Yes, all orders include free USPS Standard Shipping.'
                },
                {
                  q: 'What Does the 30-Day Return Policy Entail?',
                  a: 'You have 30 days from receiving your product to initiate a return. Please contact customer service for assistance.'
                },
                {
                  q: 'How to Reach Customer Service?',
                  a: 'Reach our customer service team anytime at (855) 684-7001 or via email at contactus@ectechsavvy.com.'
                },
              ].map((faq, i) => (
                <div key={i} className="bg-gray-50 rounded-2xl p-6 ring-1 ring-gray-100">
                  <h3 className="text-lg font-bold text-gray-900">{faq.q}</h3>
                  <p className="mt-3 text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-500 font-semibold"
              >
                See all FAQs
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
